import React, {useEffect, useMemo} from 'react';
import {CircularProgressbar, buildStyles} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

type ProgressProps = {
    percent: number;
    label?: string;
    sizePx?: number;
    textSizePx?: number;
    zIndex?: number;

    /** Включить предупреждение при закрытии/перезагрузке вкладки */
    blockNavigation?: boolean;
    /** Текст системного диалога браузера при попытке закрыть/перезагрузить */
    confirmMessage?: string;

    /** Блокировать клавиши обновления страницы (F5, Ctrl/Cmd+R) */
    blockKeys?: boolean;

    /** Блокировать клики по ссылкам (a[href]) до завершения */
    blockClicks?: boolean;
    /** Сообщение при клике по ссылке во время блокировки */
    clickMessage?: string;
};

const Progress: React.FC<ProgressProps> = ({
                                               percent,
                                               label = 'Сохранение…',
                                               sizePx = 56,
                                               textSizePx = 24,
                                               zIndex = 100002,

                                               blockNavigation = true,
                                               confirmMessage = 'Идёт сохранение. Если покинуть страницу сейчас, прогресс будет потерян.',
                                               blockKeys = true,
                                               blockClicks = true,
                                               clickMessage = 'Операция ещё идёт. Дождитесь завершения, пожалуйста.',
                                           }) => {
    const clamped = useMemo(() => Math.max(0, Math.min(100, Math.round(percent))), [percent]);
    const isDone = clamped === 100;

    // Предупреждение при закрытии/перезагрузке страницы
    useEffect(() => {
        if (isDone || !blockNavigation) return;

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            // Современные браузеры игнорируют кастомный текст, но returnValue нужен для показа диалога.
            e.returnValue = confirmMessage;
            return confirmMessage;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDone, blockNavigation, confirmMessage]);

    // Блокировка клавиш обновления (F5, Ctrl/Cmd+R)
    useEffect(() => {
        if (isDone || !blockKeys) return;

        const onKeyDown = (e: KeyboardEvent) => {
            const isF5 = e.key === 'F5';
            const isReloadCombo = (e.key === 'r' || e.key === 'R') && (e.ctrlKey || e.metaKey);
            if (isF5 || isReloadCombo) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        window.addEventListener('keydown', onKeyDown, {capture: true});
        return () => window.removeEventListener('keydown', onKeyDown, true as any);
    }, [isDone, blockKeys]);

    // Блокировка кликов по ссылкам
    useEffect(() => {
        if (isDone || !blockClicks) return;

        const onClick = (e: MouseEvent) => {
            // ищем ближайший <a href="...">
            const target = e.target as HTMLElement | null;
            const anchor = target?.closest ? (target.closest('a[href]') as HTMLAnchorElement | null) : null;
            if (!anchor) return;

            // Разрешим якорные ссылки на той же странице
            const href = anchor.getAttribute('href') || '';
            const isHashOnly = href.startsWith('#');

            if (!isHashOnly) {
                e.preventDefault();
                e.stopPropagation();
                // Неблокирующее уведомление — можно заменить на toast, если он у тебя есть в этом компоненте
                // eslint-disable-next-line no-alert
                alert(clickMessage);
            }
        };

        document.addEventListener('click', onClick, {capture: true});
        return () => document.removeEventListener('click', onClick, true as any);
    }, [isDone, blockClicks, clickMessage]);

    return (
        <div
            aria-live="polite"
            aria-modal="true"
            role="dialog"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex,
            }}
        >
            {/* Backdrop */}
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.35)',
                }}
            />

            {/* Modal */}
            <div
                style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    backgroundColor: '#fff',
                    boxShadow: '0 4px 24px rgba(114, 128, 170, 0.32)',
                    borderRadius: 8,
                    width: 256,
                    height: 96,
                    padding: '20px 24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 24,
                }}
            >
                <div style={{width: sizePx, height: sizePx}}>
                    <CircularProgressbar
                        value={clamped}
                        text={!isDone ? `${clamped}%` : ''}
                        styles={buildStyles({
                            textColor: '#000',
                            pathColor: '#0067F2',
                            trailColor: '#BADFFF',
                            textSize: `${textSizePx}px`,
                        })}
                    />
                </div>

                <div
                    style={{
                        fontWeight: 700,
                        fontSize: 16,
                        lineHeight: '20px',
                        color: '#000',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {label}
                </div>
            </div>
        </div>
    );
};

export default Progress;
